// LOCAL_TYPE_JUSTIFIED: IUsePresetOptionsArgs and IUsePresetOptionsResult are hook-local contracts; not shared across the codebase
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is a third-party npm package, not a local convex/ directory import
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type { FunctionReturnType } from 'convex/server'; // CROSS_IMPORT_JUSTIFIED: convex/server is a third-party npm package, not a local convex/ directory import
import { useOrgContext } from '@/hooks/useOrgContext';
import type { TPresetKey } from '@/@types/insuranceBuilder';
import type { IPresetOption } from '@/@types/insuranceBuilder';

type TPresetRow = FunctionReturnType<typeof api.insurance.presets.listPresetByKey>[number];

interface IUsePresetOptionsArgs {
  key: TPresetKey;
  parentValue?: string;
}

interface IUsePresetOptionsResult {
  options: IPresetOption[];
  loading: boolean;
}

export function usePresetOptions({
  key,
  parentValue,
}: IUsePresetOptionsArgs): IUsePresetOptionsResult {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const orgId = currentOrg?._id;
  const isCarModels = key === 'carModels';
  const hasParent = Boolean(parentValue);

  // For all keys except carModels: query by key only
  const byKeyResults = useQuery(
    api.insurance.presets.listPresetByKey,
    !isCarModels && orgId !== undefined ? { key, orgId } : 'skip'
  );

  // For carModels: query by key + parentKey, gated on parentValue being non-empty
  const byKeyAndParentResults = useQuery(
    api.insurance.presets.listPresetByKeyAndParent,
    isCarModels && orgId !== undefined && hasParent && parentValue
      ? { key, parentKey: parentValue, orgId }
      : 'skip'
  );

  if (isCarModels) {
    if (!hasParent) {
      return { options: [], loading: false };
    }
    const loading = orgLoading || byKeyAndParentResults === undefined;
    const rows: TPresetRow[] = byKeyAndParentResults ?? [];
    const options: IPresetOption[] = rows.map(row => ({
      label: row.label,
      value: row.value,
      parentKey: row.parentKey,
    }));
    return { options, loading };
  }

  const loading = orgLoading || byKeyResults === undefined;
  const rows: TPresetRow[] = byKeyResults ?? [];
  const options: IPresetOption[] = rows.map(row => ({
    label: row.label,
    value: row.value,
    parentKey: row.parentKey,
  }));
  return { options, loading };
}
